/**
 * Holds the auth identity the backend expects on every request as headers
 * (x-user-id / x-user-type / x-session-id). No cookie / bearer today.
 */
export interface Session {
  userId: string;
  userType: 'ORACLE' | 'CLIENT';
  sessionId: string;
  displayName?: string;
  mustChangePassword?: boolean;
  /** App permission for Oracle users; gates admin-only actions in the UI. */
  privilege?: 'ADMIN' | 'USER';
}

export interface SessionPersona {
  caseId: string;
  personaId: string;
  personaName: string;
  role: string;
}

/** True when the signed-in principal is an Oracle admin. */
export function isAdminSession(s: Session | null): boolean {
  return s?.userType === 'ORACLE' && s.privilege === 'ADMIN';
}

const KEY = 'finops.session';
const PERSONA_KEY_PREFIX = 'finops.session.persona.';
const CURRENT_PERSONA_KEY = 'finops.session.persona.current';

export function getSession(): Session | null {
  try {
    return JSON.parse(localStorage.getItem(KEY) ?? 'null') as Session | null;
  } catch {
    return null;
  }
}

export function setSession(session: Session): void {
  localStorage.setItem(KEY, JSON.stringify(session));
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
  clearAllSessionPersonas();
}

export function updateSession(patch: Partial<Session>): void {
  const current = getSession();
  if (!current) return;
  setSession({ ...current, ...patch });
}

export function setSessionPersona(
  caseId: string,
  persona: { personaId: string; personaName: string; role: string }
): void {
  const value: SessionPersona = { caseId, ...persona };
  sessionStorage.setItem(`${PERSONA_KEY_PREFIX}${caseId}`, JSON.stringify(value));
  sessionStorage.setItem(CURRENT_PERSONA_KEY, JSON.stringify(value));
}

export function getSessionPersona(caseId: string | null | undefined): SessionPersona | null {
  if (!caseId) return null;
  try {
    return JSON.parse(
      sessionStorage.getItem(`${PERSONA_KEY_PREFIX}${caseId}`) ?? 'null'
    ) as SessionPersona | null;
  } catch {
    return null;
  }
}

export function getCurrentSessionPersona(): SessionPersona | null {
  try {
    return JSON.parse(sessionStorage.getItem(CURRENT_PERSONA_KEY) ?? 'null') as SessionPersona | null;
  } catch {
    return null;
  }
}

export function clearSessionPersona(caseId?: string | null): void {
  if (caseId) {
    sessionStorage.removeItem(`${PERSONA_KEY_PREFIX}${caseId}`);
    const current = getCurrentSessionPersona();
    if (current?.caseId === caseId) {
      sessionStorage.removeItem(CURRENT_PERSONA_KEY);
    }
    return;
  }

  const current = getCurrentSessionPersona();
  if (current?.caseId) {
    sessionStorage.removeItem(`${PERSONA_KEY_PREFIX}${current.caseId}`);
  }
  sessionStorage.removeItem(CURRENT_PERSONA_KEY);
}

export function clearAllSessionPersonas(): void {
  const keys = [];
  for (let index = 0; index < sessionStorage.length; index += 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith(PERSONA_KEY_PREFIX)) {
      keys.push(key);
    }
  }
  for (const key of keys) {
    sessionStorage.removeItem(key);
  }
  sessionStorage.removeItem(CURRENT_PERSONA_KEY);
}

export function authHeaders(): Record<string, string> {
  const s = getSession();
  if (!s) return {};
  return {
    'x-user-id': s.userId,
    'x-user-type': s.userType,
    'x-session-id': s.sessionId
  };
}
